import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChartBar } from "@fortawesome/free-solid-svg-icons";
import PropTypes from "prop-types";
import { useTheme } from "../../context/ThemeContext";

/**
 * EmptyStatePanel Component
 *
 * A reusable component for displaying empty state content in analysis panels
 *
 * @param {object} props
 * @param {object} props.icon - FontAwesome icon to display (defaults to faChartBar)
 * @param {string} props.title - Heading text for the empty state
 * @param {string} props.description - Descriptive text explaining the empty state
 * @param {string} props.className - Additional CSS classes for customization
 */
const EmptyStatePanel = ({
  icon = faChartBar,
  title = "No Analysis Available",
  description = "Provide inputs to see detailed results here.",
  className = "",
}) => {
  const { effectiveTheme } = useTheme();
  const isDark = effectiveTheme === "dark";

  // Handle responsive design
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Container styles
  const containerStyles = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-start",
    padding: "1.5rem 1rem 3rem 1rem",
    color: isDark ? "#b0b0b0" : "#6b7280",
    textAlign: "center",
    height: "100%",
  };

  // Icon styles
  const iconStyles = {
    fontSize: "3rem",
    marginBottom: "1rem",
    color: isDark ? "#666666" : isMobile ? "#d1d5db" : "#9ca3af",
  };

  // Title styles
  const titleStyles = {
    fontSize: isMobile ? "1.125rem" : "1.1rem",
    margin: "0 0 0.5rem 0",
    color: isDark ? "#e0e0e0" : "#374151",
  };

  // Description styles
  const descriptionStyles = {
    margin: "0",
    lineHeight: "1.6",
    color: isDark ? "#b0b0b0" : "#6b7280",
    fontSize: isMobile ? "0.875rem" : "0.938rem",
  };

  return (
    <div className={className} style={containerStyles}>
      <FontAwesomeIcon icon={icon} style={iconStyles} />
      <h3 style={titleStyles}>{title}</h3>
      <p style={descriptionStyles}>{description}</p>
    </div>
  );
};

EmptyStatePanel.propTypes = {
  icon: PropTypes.object,
  title: PropTypes.string,
  description: PropTypes.string,
  className: PropTypes.string,
};

export default EmptyStatePanel;
